import { styled } from '@porsche-code/kado-styled-system/jsx';
import { ActionGroup, DataTable, Spacer } from '@porsche-code/kado-ui';
import { createDataTableColumnHelper as createColumnHelper } from '@porsche-code/kado-ui';
import {
  PHeading,
  PIcon,
  PInlineNotification,
  PLinkPure,
  PTag,
} from '@porsche-design-system/components-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ClientError } from 'graphql-request';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ApplicationTags, DateOutput } from '@/components';
import { App, APP_FALLBACK } from '@/config/app';
import { NAMESPACES } from '@/config/i18n';
import { useAdminFeatureArticlesQuery } from '@/graphql';
import { useApplicationConfigs } from '@/hooks';

import {
  CreateNewButton,
  DeleteButton,
  EditButton,
  PublishSwitch,
} from './-components';

export const Route = createFileRoute('/admin/news/_auth/')({
  component: News,
  beforeLoad: () => {
    return { breadcrumb: () => <Trans i18nKey="featureArticles" /> };
  },
});

const PAGE_SIZE = 10;

function News() {
  const {
    t,
    i18n: { language },
  } = useTranslation(NAMESPACES);

  const appConfigs = useApplicationConfigs();

  const [error, setError] = useState<unknown>();
  const [now] = useState(() => Date.now());

  const { isPending, data: queryData } = useAdminFeatureArticlesQuery();

  const featureArticles = useMemo(
    () =>
      queryData?.featureArticles.map((feature) => ({
        ...feature,
        apps: feature.apps.length > 0 ? feature.apps : [APP_FALLBACK],
      })),
    [queryData],
  );

  const columns = useMemo(() => {
    const columnHelper =
      createColumnHelper<NonNullable<typeof featureArticles>[number]>();

    return [
      columnHelper.accessor('publishedAt', {
        cell: (data) => {
          const publishDate = data.getValue();
          const isLive =
            !!data.row.original.isPublished &&
            !!publishDate &&
            new Date(publishDate).getTime() <= now;

          return (
            <PublishContainer>
              {isLive ? (
                <PIcon name="check" color="success" />
              ) : (
                <PIcon name="clock" />
              )}
              {publishDate ? <DateOutput date={publishDate} /> : 'n/a'}
            </PublishContainer>
          );
        },
        header: () => t('publishDate'),
        enableColumnFilter: false,
      }),
      columnHelper.accessor('title', {
        cell: (data) => data.getValue(),
        header: () => t('featureArticle.title'),
      }),
      columnHelper.accessor('author.name', {
        cell: (data) => data.getValue(),
        header: () => t('author'),
      }),
      columnHelper.accessor('apps', {
        cell: (data) => (
          <ApplicationTags apps={data.getValue()} variant="primary" />
        ),
        header: () => t('apps'),
        meta: {
          filterType: 'select',
          filterOptions: [
            ...new Set(featureArticles?.flatMap((article) => article.apps)),
          ].map((app) => ({
            label: appConfigs[app as App].name,
            value: app,
          })),
        },
        filterFn: (row, columnId, filterValue) =>
          (row.getValue(columnId) as string[]).includes(filterValue),
      }),
      columnHelper.accessor('roles', {
        cell: (data) => (
          <RoleTagsContainer>
            {data.getValue().map((role) => (
              <PTag key={`${data.row.original.id}_${role}`}>{role}</PTag>
            ))}
          </RoleTagsContainer>
        ),
        header: () => t('roles'),
        meta: {
          filterType: 'select',
          filterOptions: [
            ...new Set(featureArticles?.flatMap((article) => article.roles)),
          ].map((role) => ({
            label: role,
            value: role,
          })),
        },
        filterFn: (row, columnId, filterValue) =>
          (row.getValue(columnId) as string[]).includes(filterValue),
      }),
      columnHelper.accessor(
        (row) => {
          const orgs = row.organizations.map(
            (organization) => organization.name,
          );

          if (orgs.length > 4) {
            orgs.splice(3, 0, t('xMore', { count: orgs.length - 3 }));
          }

          return new Intl.ListFormat(language, { type: 'conjunction' }).format(
            orgs.slice(0, 4),
          );
        },
        {
          id: 'organizations',
          header: () => t('organizations'),
          meta: {
            name: t('organizations'),
          },
          filterFn: 'includesString',
        },
      ),
      columnHelper.accessor('isPublished', {
        cell: (data) => (
          <PublishSwitch
            articleId={data.row.original.id}
            checked={!!data.getValue()}
          />
        ),
        header: () => t('published'),
        meta: {
          filterType: 'select',
          filterOptions: [true, false].map((value) => ({
            label: value ? t('published') : t('notPublished'),
            value,
          })),
        },
        filterFn: (row, columnId, filterValue) =>
          `${row.getValue(columnId)}` === filterValue,
      }),
      columnHelper.accessor('id', {
        header: () => (
          <TableHeaderActions>{t('common:actions')}</TableHeaderActions>
        ),
        cell: (data) => {
          const id = data.getValue();
          return (
            <TableCellActions>
              <PLinkPure
                size="sm"
                aria-label={t('previewArticle')}
                icon="view"
                hideLabel
              >
                <Link
                  to="/news/$featureArticleId"
                  params={{ featureArticleId: id }}
                  search={{ preview: 1 }}
                />
              </PLinkPure>
              <EditButton articleId={id} onError={(err) => setError(err)} />
              <DeleteButton articleId={id} onError={(err) => setError(err)} />
            </TableCellActions>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ];
  }, [featureArticles, t, appConfigs, language, now]);

  return (
    <>
      <PHeading aria-level={1} tag="h1" size="lg">
        {t('featureArticles')}
      </PHeading>
      <Spacer mb="md" />
      {error && (
        <>
          <PInlineNotification
            heading={t('featureArticle.error')}
            state="error"
            onDismiss={() => setError(undefined)}
          >
            {error instanceof ClientError
              ? t((error.response.errors?.[0].message as any) ?? error.message)
              : `${error}`}
          </PInlineNotification>

          <Spacer mb="md" />
        </>
      )}
      <ActionGroup>
        <div />
        <CreateNewButton onError={(err) => setError(err)} />
      </ActionGroup>
      <Spacer mb="md" />
      <DataTable
        caption={t('featureArticles')}
        isLoading={isPending}
        data={featureArticles ?? []}
        pagination={{
          pageSize: PAGE_SIZE,
        }}
        columns={columns}
        idAccessor="id"
        i18n={{
          filterLabel: (columnName) =>
            t('common:iconLabel.filter', { columnName }),
          optionAll: t('common:all'),
          buttonReset: t('common:action.reset'),
          buttonFilter: t('common:action.filter'),
          actionSearch: t('common:action.search'),
          noData: t('common:noData'),
          minLabel: t('common:min'),
          maxLabel: t('common:max'),
        }}
      />
    </>
  );
}

const TableCellActions = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'end',
    gap: 'sm',
  },
});

const TableHeaderActions = styled('div', {
  base: {
    textAlign: 'right',
  },
});

const RoleTagsContainer = styled('div', {
  base: {
    display: 'inline-grid',
    gap: 'sm',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
  },
});

const PublishContainer = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: 'sm',
  },
});
